import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircleIcon, PackageIcon, TruckIcon, HomeIcon, MapPinIcon, CreditCardIcon, TagIcon, ClockIcon, StoreIcon, PhoneIcon, ExternalLinkIcon, NavigationIcon, ChevronRightIcon, SnowflakeIcon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { HelmetProvider } from 'react-helmet-async';
import { SEO } from '../components/shared/SEO';
import { Header } from '../components/layout/Header';
import { Footer } from '../components/layout/Footer';
import { MobileFooterNav } from '../components/layout/MobileFooterNav';
import { PharmacistChat } from '../components/services/PharmacistChat';
import { AnnouncementBar } from '../components/layout/AnnouncementBar';
import { MobileDeliverToPill } from '../components/layout/MobileDeliverToPill';
import { PageHeroBreadcrumb } from '../components/layout/PageHeroBreadcrumb';
import { MobileHomeHeader } from '../components/MobileHomeHeader';
import { OrderRatingModal } from '../components/modals/OrderRatingModal';
import { PRODUCT_IMAGES } from '../data/productImages';
export function ThankYouPage() {
  const navigate = useNavigate();
  const [confettiActive, setConfettiActive] = useState(true);
  const [timeLeft, setTimeLeft] = useState({
    hours: 0,
    minutes: 0,
    seconds: 0
  });
  const [showDeliveryTracking, setShowDeliveryTracking] = useState(false);
  const [showRating, setShowRating] = useState(false);

  // Scroll to top on mount and show rating after delay
  useEffect(() => {
    window.scrollTo(0, 0);
    const timer = setTimeout(() => {
      setShowRating(true);
    }, 2000);
    return () => clearTimeout(timer);
  }, []);
  const [locationModalOpen, setLocationModalOpen] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState<{
    name: string;
  } | null>(null);

  // Mock order data
  const orderData = {
    orderNumber: 'DN38291',
    orderDate: 'December 20, 2024',
    orderTime: '2:30 PM',
    deliveryType: 'delivery',
    coldChain: true,
    temperatureRange: '2–8°C',
    estimatedDelivery: 'Within 120 minutes',
    deliveryDate: 'December 20, 2024',
    deliveryTime: '6:00 PM - 8:00 PM',
    shippingAddress: {
      name: 'Ahmed Mohammed',
      phone: '+966 50 123 4567',
      address: 'Al Malqa District, Building 123, Apt 4B',
      city: 'Riyadh',
      postalCode: '12345'
    },
    pickupBranch: {
      name: 'Innova Pharmacy - Riyadh Park',
      address: 'Riyadh Park Mall, Al Malaz District, Riyadh',
      city: 'Riyadh',
      district: 'Al Malaz',
      phone: '+966 11 234 5678',
      coordinates: {
        lat: 24.7136,
        lng: 46.6753
      },
      hours: {
        open: '08:00 AM',
        close: '11:00 PM'
      },
      pickupTime: 'Today, 4:00 PM - 8:00 PM'
    },
    paymentMethod: {
      type: 'Credit Card',
      last4: '4242',
      brand: 'Visa'
    },
    items: [{
      id: 1,
      name: 'Optimum Nutrition Gold Standard 100% Whey Protein',
      quantity: 2,
      price: 29.99,
      image: 'https://img.innovapharmacy.com/media/catalog/product/4/5/4537b3e3-4206-41fc-b34e-53ddce591f66_1.jpg'
    }, {
      id: 2,
      name: 'C4 Original Pre Workout Powder',
      quantity: 1,
      price: 19.99,
      image: 'https://img.innovapharmacy.com/media/catalog/product/2/1/213099.jpg'
    }],
    pricing: {
      subtotal: 79.97,
      shipping: 0,
      discount: 10.0,
      tax: 12.0,
      total: 81.97
    },
    coupon: {
      code: 'WELCOME10',
      discount: 10.0
    }
  };
  const getGoogleMapsLink = () => {
    if (orderData.deliveryType === 'pickup' && orderData.pickupBranch) {
      const {
        lat,
        lng
      } = orderData.pickupBranch.coordinates;
      return `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;
    }
    return null;
  };
  return <HelmetProvider data-id="element-9268" data-mp="fuvp79z14y3v">
      <SEO title="Order Confirmed - Thank You" description="Your order has been successfully placed. Thank you for shopping with Innova Pharmacy." canonical="/en/thank-you/" hreflangAr="/thank-you/" hreflangEn="/en/thank-you/" hreflangDefault="/thank-you/" data-id="element-9269" data-mp="fy9og9cecr73" />
      <div className="flex flex-col min-h-screen bg-gray-50/50" data-id="element-9270" data-mp="3u8phuso1fdcc">
        {/* Desktop Header */}
        <div className="hidden md:block" data-id="element-9271" data-mp="3q7nqey0d45kx">
          <AnnouncementBar data-id="element-9272" data-mp="3laniehjycsr6" />
          <MobileDeliverToPill selectedLocation={selectedLocation} onOpenLocationModal={() => setLocationModalOpen(true)} data-id="element-9273" data-mp="3lbylsg9hjyhr" />
          <Header locationModalOpen={locationModalOpen} setLocationModalOpen={setLocationModalOpen} selectedLocation={selectedLocation} setSelectedLocation={setSelectedLocation} data-id="element-9274" data-mp="3bboaj170h158" />
        </div>

        <main className="flex-grow pt-[var(--header-height-mobile)] md:pt-[var(--header-height-desktop)]" data-id="element-9275" data-mp="19iqobo7qp6t9">
          {/* Mobile Header Overlay - Only on mobile */}
          <div className="md:hidden fixed top-0 left-0 right-0 z-50" data-id="element-9276" data-mp="2nftee8r0dwy2">
            <MobileHomeHeader selectedLocation={selectedLocation} onOpenLocationModal={() => setLocationModalOpen(true)} alwaysWhiteBackground={true} data-id="element-9277" data-mp="2sfpdpuorh07a" />
          </div>

          {/* Page Hero Breadcrumb */}
          <PageHeroBreadcrumb breadcrumbs={[{
          label: 'Home',
          href: '/'
        }, {
          label: 'Order Confirmed'
        }]} title="Thank You for Your Order" description="Your order has been successfully placed" data-id="element-9278" data-mp="31adlbrht6tz9" />

          {/* Main Content */}
          <div className="py-8 px-4" data-id="element-9279" data-mp="2ij2ypmj493d6">
            <div className="max-w-5xl mx-auto" data-id="element-9280" data-mp="3oy3tvq0bhhip">
              {/* Success Header */}
              <div className="bg-white rounded-2xl p-8 mb-6 text-center shadow-sm border border-gray-100" data-id="element-9281" data-mp="3twybiomjt018">
                <div className="relative mb-8 flex justify-center" data-id="element-9282" data-mp="1tuf9l7v0cg9y">
                  <motion.div initial={{
                  y: -20,
                  opacity: 0
                }} animate={{
                  y: 0,
                  opacity: 1
                }} transition={{
                  type: 'spring',
                  bounce: 0.5,
                  duration: 0.8
                }} className="w-24 h-24 bg-[#FFD6E5] rounded-2xl flex items-center justify-center border-2 border-gray-900 shadow-[4px_4px_0px_0px_rgba(17,24,39,1)]" data-id="element-9283" data-mp="1tws1acqki507">
                    <PackageIcon className="w-12 h-12 text-white fill-white" strokeWidth={1} data-id="element-9284" data-mp="1b24nuqshiyz2" />
                  </motion.div>
                  <motion.div initial={{
                  scale: 0
                }} animate={{
                  scale: 1
                }} transition={{
                  delay: 0.5,
                  type: 'spring',
                  bounce: 0.5
                }} className="absolute -bottom-3 ml-16 bg-[#A3E635] rounded-full p-1 border-2 border-gray-900" data-id="element-9285" data-mp="1ku0p2apvb57i">
                    <CheckCircleIcon className="w-6 h-6 text-gray-900" strokeWidth={2.5} data-id="element-9286" data-mp="nfemzsrvhag1" />
                  </motion.div>
                </div>
                
                <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3" data-id="element-9287" data-mp="2z05dxzoryghl">
                  Thanks for your order!
                </h1>
                <p className="text-gray-600 mb-6 max-w-md mx-auto" data-id="element-9288" data-mp="2q2bl4yq7y4z6">
                  Did you know? Spending more than SAR 250 will get you 5% cashback on your next purchase.
                </p>
                <div className="inline-flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-xl px-5 py-2.5" data-id="element-9289" data-mp="3hs7wjgkz86uh">
                  <span className="text-sm text-gray-600" data-id="element-9290" data-mp="3mr130p1uixl5">Order Number:</span>
                  <span className="text-lg font-bold text-[#1E2B55]" data-id="element-9291" data-mp="38qqalhkw1axv">
                    #{orderData.orderNumber}
                  </span>
                </div>
              </div>

              {/* Main Content Grid */}
              <div className="grid lg:grid-cols-3 gap-6" data-id="element-9292" data-mp="1xb1o0rbjqdxl">
                {/* Left Column - Order Details */}
                <div className="lg:col-span-2 space-y-6" data-id="element-9293" data-mp="22d0tq7urdmxp">
                  {/* Delivery Type Badge */}
                  <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100" data-id="element-9294" data-mp="2szokalcoporq">
                    <div className="flex items-center justify-center gap-4" data-id="element-9295" data-mp="2oywb1s3tbjvg">
                      {orderData.deliveryType === 'delivery' ? <>
                          <div className="w-14 h-14 bg-blue-50 rounded-2xl flex items-center justify-center" data-id="element-9296" data-mp="2f3bei9hijbt3">
                            <TruckIcon className="w-7 h-7 text-blue-600" data-id="element-9297" data-mp="3cp8t3vsruwzr" />
                          </div>
                          <div className="text-left" data-id="element-9298" data-mp="37rh0ezkaz6ui">
                            <h3 className="text-xl font-bold text-gray-900" data-id="element-9299" data-mp="32zfdmjpidxq8">
                              Home Delivery
                            </h3>
                            <p className="text-sm text-gray-500 mt-1" data-id="element-9300" data-mp="2xyrs2dk1u93e">
                              Your order will be delivered to your address
                            </p>
                          </div>
                        </> : <>
                          <div className="w-14 h-14 bg-green-50 rounded-2xl flex items-center justify-center" data-id="element-9301" data-mp="1x4oijcqidgw">
                            <StoreIcon className="w-7 h-7 text-green-600" data-id="element-9302" data-mp="6whf9f6q84or" />
                          </div>
                          <div className="text-left" data-id="element-9303" data-mp="bvu9ikzxge1d">
                            <h3 className="text-xl font-bold text-gray-900" data-id="element-9304" data-mp="gtca9o3sjmya">
                              Store Pickup
                            </h3>
                            <p className="text-sm text-gray-500 mt-1" data-id="element-9305" data-mp="pt9381x7r7z5">
                              Pick up your order from the selected branch
                            </p>
                          </div>
                        </>}
                    </div>
                  </div>

                  {orderData.coldChain && <section className="rounded-xl border border-[#b8e2df] bg-[#f2fbfa] p-4" aria-label="Temperature-protected delivery confirmation" data-id="element-9306" data-mp="14jgffzwmnure">
                      <div className="flex items-start gap-3" data-id="element-9307" data-mp="3ratr0zm7fr4r"><span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[#16726c] text-white" data-id="element-9308" data-mp="3rbv2v6b1ohnj"><SnowflakeIcon className="h-5 w-5" data-id="element-9309" data-mp="50raeiprus7" /></span><div data-id="element-9310" data-mp="53c0cn16q19d"><h2 className="text-sm font-bold text-[#173c3a]" data-id="element-9311" data-mp="53c0qo53k39w">Temperature-protected delivery confirmed</h2><p className="mt-1 text-xs leading-5 text-[#416b67]" data-id="element-9312" data-mp="9u24jwxvjy2h">Eligible items will be packed for {orderData.temperatureRange} delivery. Follow packing, vehicle and temperature status before handoff.</p><button type="button" onClick={() => navigate('/track-order?mode=now')} className="mt-3 text-xs font-bold text-[#16726c]" data-id="element-9313" data-mp="evhplp01y4kv">Track protected delivery</button></div></div>
                    </section>}

                  {/* Order Timeline */}
                  <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100" data-id="element-9314" data-mp="ss4z0r96yn7o">
                    <h2 className="text-lg font-bold text-gray-900 mb-6 flex items-center gap-2" data-id="element-9315" data-mp="2pmrew3hdmp66">
                      <ClockIcon className="w-5 h-5 text-[#1E2B55]" data-id="element-9316" data-mp="2ukt3f3xkngp1" />
                      Order Timeline
                    </h2>
                    <div className="space-y-6" data-id="element-9317" data-mp="2ia1vetailti6">
                      <div className="flex items-start gap-4" data-id="element-9318" data-mp="2i99ynf56lpqb">
                        <div className="w-10 h-10 bg-green-50 rounded-xl flex items-center justify-center flex-shrink-0" data-id="element-9319" data-mp="2dap8k8jn8mfl">
                          <CheckCircleIcon className="w-5 h-5 text-green-600" data-id="element-9320" data-mp="28cnhp1fz7itk" />
                        </div>
                        <div className="flex-1 pt-2" data-id="element-9321" data-mp="35yl06yucvo5i">
                          <h3 className="font-bold text-gray-900" data-id="element-9322" data-mp="310jk8ms884y9">
                            Order Confirmed
                          </h3>
                          <p className="text-sm text-gray-500 mt-1" data-id="element-9323" data-mp="2w0ezulo76q2r">
                            {orderData.orderDate} at {orderData.orderTime}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-start gap-4" data-id="element-9324" data-mp="1knkxbltyjbvd">
                        <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center flex-shrink-0" data-id="element-9325" data-mp="1fm5cnuvoz7di">
                          <PackageIcon className="w-5 h-5 text-blue-600" data-id="element-9326" data-mp="7o9cxohzssd5" />
                        </div>
                        <div className="flex-1 pt-2" data-id="element-9327" data-mp="bojttw7bp36s">
                          <h3 className="font-bold text-gray-900" data-id="element-9328" data-mp="3tvhgqmd9kr72">
                            Processing Order
                          </h3>
                          <p className="text-sm text-gray-500 mt-1" data-id="element-9329" data-mp="2mts9xjq8nva">
                            We're preparing your items
                          </p>
                        </div>
                      </div>
                      <div className="flex items-start gap-4" data-id="element-9330" data-mp="3oxzd9bi0n46g">
                        <div className="w-10 h-10 bg-gray-50 rounded-xl flex items-center justify-center flex-shrink-0" data-id="element-9331" data-mp="3b5j9u9l7d450">
                          {orderData.deliveryType === 'delivery' ? <TruckIcon className="w-5 h-5 text-gray-400" data-id="element-9332" data-mp="317brbmb9me0w" /> : <StoreIcon className="w-5 h-5 text-gray-400" data-id="element-9333" data-mp="3642708j5r7ls" />}
                        </div>
                        <div className="flex-1 pt-2" data-id="element-9334" data-mp="2mlo76152b9i1">
                          <h3 className="font-medium text-gray-600" data-id="element-9335" data-mp="2iirrojatjtib">
                            {orderData.deliveryType === 'delivery' ? 'Estimated Delivery' : 'Ready for Pickup'}
                          </h3>
                          <p className="text-sm text-gray-400 mt-1" data-id="element-9336" data-mp="35i7kz3qt9lkx">
                            {orderData.deliveryType === 'delivery' ? orderData.deliveryDate : orderData.pickupBranch?.pickupTime}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Order Items */}
                  <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100" data-id="element-9337" data-mp="3p7hycb333ufv">
                    <h2 className="text-lg font-bold text-gray-900 mb-6 flex items-center gap-2" data-id="element-9338" data-mp="3k8etaqn3to7w">
                      <PackageIcon className="w-5 h-5 text-[#1E2B55]" data-id="element-9339" data-mp="1hjna86dhl1ko" />
                      Order Items
                    </h2>
                    <div className="space-y-4" data-id="element-9340" data-mp="1mhykgi7p1e3f">
                      {orderData.items.map(item => <div key={item.id} className="flex gap-4 pb-4 border-b border-gray-100 last:border-0 last:pb-0" data-id="element-9341" data-mp="1rb9qwi8lfsut">
                          <div className="w-20 h-20 bg-gray-50 rounded-xl p-2 flex-shrink-0" data-id="element-9342" data-mp="ynni8vkidy13">
                            <img src={item.image} alt={item.name} className="w-full h-full object-contain mix-blend-multiply" data-id="element-9343" data-mp="13l5p8geyu36g" />
                          </div>
                          <div className="flex-1" data-id="element-9344" data-mp="2jydqg7ox3ez7">
                            <h3 className="font-medium text-gray-900 mb-1 line-clamp-2" data-id="element-9345" data-mp="2oy8g67wyhyio">
                              {item.name}
                            </h3>
                            <p className="text-sm text-gray-500" data-id="element-9346" data-mp="da3nmuz7snaq">
                              Qty: {item.quantity}
                            </p>
                          </div>
                          <div className="text-right" data-id="element-9347" data-mp="3jsk39jg0g4d">
                            <p className="font-bold text-gray-900" data-id="element-9348" data-mp="3urekmtav82h0">
                              SAR {(item.price * item.quantity).toFixed(2)}
                            </p>
                          </div>
                        </div>)}
                    </div>
                  </div>

                  {/* Shipping Address or Pickup Branch Details */}
                  {orderData.deliveryType === 'delivery' ? <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100" data-id="element-9349" data-mp="3btk9o9q6fgvz">
                      <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2" data-id="element-9350" data-mp="1ez7fw9zx11xw">
                        <MapPinIcon className="w-5 h-5 text-[#1E2B55]" data-id="element-9351" data-mp="1a276bp3uxh21" />
                        Shipping Address
                      </h2>
                      <div className="space-y-1" data-id="element-9352" data-mp="34fjg6hza0h8f">
                        <p className="font-bold text-gray-900" data-id="element-9353" data-mp="3995usfp02v6v">
                          {orderData.shippingAddress.name}
                        </p>
                        <p className="text-gray-600" data-id="element-9354" data-mp="2uhdh084utp79">
                          {orderData.shippingAddress.phone}
                        </p>
                        <p className="text-gray-600 mt-2" data-id="element-9355" data-mp="3n7m1i6mv4lp0">
                          {orderData.shippingAddress.address}
                        </p>
                        <p className="text-gray-600" data-id="element-9356" data-mp="3s4vwe1t0tpk8">
                          {orderData.shippingAddress.city},{' '}
                          {orderData.shippingAddress.postalCode}
                        </p>
                      </div>
                    </div> : <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100" data-id="element-9357" data-mp="21t7upgy5js5b">
                      <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2" data-id="element-9358" data-mp="26ux8v5go8lxm">
                        <StoreIcon className="w-5 h-5 text-[#1E2B55]" data-id="element-9359" data-mp="ou6139f23jtu" />
                        Pickup Branch Details
                      </h2>
                      <div className="space-y-4" data-id="element-9360" data-mp="xx74v3gg8yuc">
                        {/* Branch Name */}
                        <div data-id="element-9361" data-mp="tvd8v8h78j0i">
                          <p className="text-sm text-gray-500 mb-1" data-id="element-9362" data-mp="tw50y9bl7ym1">
                            Branch Name
                          </p>
                          <p className="font-bold text-gray-900 text-lg" data-id="element-9363" data-mp="63eq63ff7ldc">
                            {orderData.pickupBranch?.name}
                          </p>
                        </div>
                        {/* Location */}
                        <div data-id="element-9364" data-mp="a6kxzfi6lklx">
                          <p className="text-sm text-gray-500 mb-1" data-id="element-9365" data-mp="a6b6fnhhn5e4">Location</p>
                          <div className="flex items-start gap-2" data-id="element-9366" data-mp="3im4ldxw688t2">
                            <MapPinIcon className="w-5 h-5 text-gray-600 mt-0.5 flex-shrink-0" data-id="element-9367" data-mp="3elax33etgrzn" />
                            <div data-id="element-9368" data-mp="7apkhngjbw09">
                              <p className="text-gray-900 font-medium" data-id="element-9369" data-mp="7bhckoaxbbls">
                                {orderData.pickupBranch?.address}
                              </p>
                              <p className="text-sm text-gray-600 mt-1" data-id="element-9370" data-mp="gbnqgbajadlh">
                                {orderData.pickupBranch?.district},{' '}
                                {orderData.pickupBranch?.city}
                              </p>
                            </div>
                          </div>
                        </div>
                        {/* Phone */}
                        <div data-id="element-9371" data-mp="v6k454143t5y">
                          <p className="text-sm text-gray-500 mb-1" data-id="element-9372" data-mp="zzmy3d9sx4mo">
                            Branch Phone
                          </p>
                          <div className="flex items-center gap-2" data-id="element-9373" data-mp="140gpigmgd1kb">
                            <PhoneIcon className="w-5 h-5 text-gray-600" data-id="element-9374" data-mp="190un14qfgt43" />
                            <a href={`tel:${orderData.pickupBranch?.phone}`} className="text-[#1E2B55] hover:underline font-medium" data-id="element-9375" data-mp="1dz5z7m473fph">
                              {orderData.pickupBranch?.phone}
                            </a>
                          </div>
                        </div>
                        {/* Working Hours */}
                        <div data-id="element-9376" data-mp="1a3ts8v99r0ql">
                          <p className="text-sm text-gray-500 mb-1" data-id="element-9377" data-mp="1a4weuqzk6h0p">
                            Working Hours
                          </p>
                          <div className="flex items-center gap-2" data-id="element-9378" data-mp="21uhnxrs6bdwv">
                            <ClockIcon className="w-5 h-5 text-gray-600" data-id="element-9379" data-mp="21v9jx3pondno" />
                            <p className="text-gray-900 font-medium" data-id="element-9380" data-mp="1x2py5jgudc7s">
                              {orderData.pickupBranch?.hours.open} -{' '}
                              {orderData.pickupBranch?.hours.close}
                            </p>
                          </div>
                        </div>
                        {/* Pickup Time */}
                        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4" data-id="element-9381" data-mp="2kriyds4hy687">
                          <p className="text-sm text-gray-600 mb-1" data-id="element-9382" data-mp="f6gw9zhu356r">
                            Estimated Pickup Time
                          </p>
                          <p className="text-lg font-bold text-[#1E2B55]" data-id="element-9383" data-mp="696k8bx6zc0s">
                            {orderData.pickupBranch?.pickupTime}
                          </p>
                        </div>
                        {/* Google Maps Link */}
                        {getGoogleMapsLink() && <a href={getGoogleMapsLink() || '#'} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-2 w-full bg-[#1E2B55] hover:bg-[#162041] text-white font-medium py-3 rounded-xl transition-colors" data-id="element-9384" data-mp="tyr3yx166db4">
                            <NavigationIcon className="w-5 h-5" data-id="element-9385" data-mp="1cwaek53a03zn" />
                            Get Directions on Google Maps
                            <ExternalLinkIcon className="w-4 h-4" data-id="element-9386" data-mp="1k99p766x5cux" />
                          </a>}
                      </div>
                    </div>}

                  {/* Payment Method */}
                  <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100" data-id="element-9387" data-mp="1u88z0s3f7eji">
                    <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2" data-id="element-9388" data-mp="1z2mokdveirg9">
                      <CreditCardIcon className="w-5 h-5 text-[#1E2B55]" data-id="element-9389" data-mp="234qc40vchmhw" />
                      Payment Method
                    </h2>
                    <div className="flex items-center gap-4" data-id="element-9390" data-mp="2cw46xvccpy4p">
                      <div className="w-14 h-10 bg-gray-50 border border-gray-200 rounded-lg flex items-center justify-center" data-id="element-9391" data-mp="2cyqf9x72ghnz">
                        <CreditCardIcon className="w-6 h-6 text-gray-600" data-id="element-9392" data-mp="2mv3grh8urzta" />
                      </div>
                      <div data-id="element-9393" data-mp="2qvnbke5yerc9">
                        <p className="font-bold text-gray-900" data-id="element-9394" data-mp="2qwyeycvhlx2u">
                          {orderData.paymentMethod.brand}{' '}
                          {orderData.paymentMethod.type}
                        </p>
                        <p className="text-sm text-gray-500" data-id="element-9395" data-mp="mu5ajtfd551g">
                          •••• •••• •••• {orderData.paymentMethod.last4}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Right Column - Order Summary */}
                <div className="lg:col-span-1" data-id="element-9396" data-mp="1ajzko38asfi0">
                  <div className="bg-white rounded-2xl p-6 sticky top-6 shadow-sm border border-gray-100" data-id="element-9397" data-mp="15jlaofn4yvhc">
                    <h2 className="text-lg font-bold text-gray-900 mb-6" data-id="element-9398" data-mp="1jhjqzql0p1zh">
                      Order Summary
                    </h2>
                    <div className="space-y-4 mb-6" data-id="element-9399" data-mp="1fevf1a10beya">
                      <div className="flex justify-between text-gray-600" data-id="element-9400" data-mp="3l969t4ur9qkv">
                        <span data-id="element-9401" data-mp="3h8mapvqmgcw3">Subtotal</span>
                        <span className="font-medium text-gray-900" data-id="element-9402" data-mp="3h9nwov96ejsl">SAR {orderData.pricing.subtotal.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between text-gray-600" data-id="element-9403" data-mp="3b5ztl53s1ppt">
                        <span data-id="element-9404" data-mp="3b83wbjpxwioi">Shipping</span>
                        <span className="text-green-600 font-medium" data-id="element-9405" data-mp="2xf4jjphjl2kn">
                          {orderData.pricing.shipping === 0 ? 'FREE' : `SAR ${orderData.pricing.shipping.toFixed(2)}`}
                        </span>
                      </div>
                      {orderData.coupon && <div className="flex justify-between text-green-600" data-id="element-9406" data-mp="2sgt5f2k7s5wm">
                          <span className="flex items-center gap-1" data-id="element-9407" data-mp="2eh1whg8k1fph">
                            <TagIcon className="w-4 h-4" data-id="element-9408" data-mp="2jebnv1fi81fy" />
                            Coupon ({orderData.coupon.code})
                          </span>
                          <span className="font-medium" data-id="element-9409" data-mp="cmarxqpb5cpo">
                            -SAR {orderData.coupon.discount.toFixed(2)}
                          </span>
                        </div>}
                      <div className="flex justify-between text-gray-600" data-id="element-9410" data-mp="2do8xn0zj3o0x">
                        <span data-id="element-9411" data-mp="28sbfqq8ma9m1">Tax (VAT 15%)</span>
                        <span className="font-medium text-gray-900" data-id="element-9412" data-mp="23rw3y33zha66">SAR {orderData.pricing.tax.toFixed(2)}</span>
                      </div>
                      <div className="border-t border-gray-100 pt-4" data-id="element-9413" data-mp="1zqaivuhappkw">
                        <div className="flex justify-between items-center" data-id="element-9414" data-mp="1us0sdx6ind5m">
                          <span className="text-lg font-bold text-gray-900" data-id="element-9415" data-mp="1q1ajwb2smu6p">
                            Total
                          </span>
                          <span className="text-2xl font-bold text-[#1E2B55]" data-id="element-9416" data-mp="1l2h58fbrsx4y">
                            SAR {orderData.pricing.total.toFixed(2)}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="space-y-3" data-id="element-9417" data-mp="172ofonbzcmyk">
                      <button onClick={() => navigate('/')} className="w-full bg-black hover:bg-gray-800 text-white font-bold py-3.5 rounded-xl transition-colors flex items-center justify-center gap-2" data-id="element-9418" data-mp="3iprybz4fzhqd">
                        Continue Shopping
                      </button>
                      <button onClick={() => navigate('/account/orders')} className="w-full bg-gray-50 text-gray-900 hover:bg-gray-100 font-bold py-3.5 rounded-xl transition-colors" data-id="element-9419" data-mp="jdexknemfkl">
                        Track Your Order
                      </button>
                    </div>

                    {/* Need Help */}
                    <div className="mt-6 pt-6 border-t border-gray-100 text-center" data-id="element-9420" data-mp="3dr6zo44u9641">
                      <p className="text-sm text-gray-500 mb-2" data-id="element-9421" data-mp="2g9zxp2q3v5hg">
                        Need help with your order?
                      </p>
                      <button className="text-[#1E2B55] hover:text-[#162041] font-bold text-sm" data-id="element-9422" data-mp="2l81d9dobomo6">
                        Contact Customer Support
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>

        <div className="hidden md:block" data-id="element-9423" data-mp="3tbqhq9gbz33m">
          <Footer data-id="element-9424" data-mp="3tct90ihkf7k2" />
        </div>
        <MobileFooterNav data-id="element-9425" data-mp="3tcjp9cn45wmt" />
        <PharmacistChat data-id="element-9426" data-mp="35m5zxtvwybvx" />
        <div className="h-16 md:hidden" data-id="element-9427" data-mp="35le20ceug22h"></div>

        <OrderRatingModal isOpen={showRating} orderNumber={orderData.orderNumber} onClose={() => setShowRating(false)} onSubmit={rating => {
        sessionStorage.setItem(`order-rating-${orderData.orderNumber}`, String(rating));
      }} data-id="element-9428" data-mp="35o9yrxeygkpc" />
      </div>
    </HelmetProvider>;
}